import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    GenderDashboardFilter,
    GenderDistributionDto,
    GenderYearTotalDto,
    GenderGrandTotalDto
} from 'generated/openApi';
import ApiService from 'services/apiService';

interface GenderDataContextType {
    data: GenderDistributionDto[];
    loading: boolean;
    error: string | null;
    filter: GenderDashboardFilter;
    activeSchoolYear: number | null;
    totalsByYear: GenderYearTotalDto[];
    grandTotal: GenderGrandTotalDto | null;
    updateFilter: (filter: Partial<GenderDashboardFilter>) => void;
    refetch: () => void;
}

const GenderDataContext = createContext<GenderDataContextType | undefined>(undefined);

export const GenderDataProvider = ({ children }: { children: React.ReactNode }) => {
    const [data, setData] = useState<GenderDistributionDto[]>([]);
    const [totalsByYear, setTotalsByYear] = useState<GenderYearTotalDto[]>([]);
    const [grandTotal, setGrandTotal] = useState<GenderGrandTotalDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);
    const [filter, setFilter] = useState<GenderDashboardFilter>({
        schoolYears: [],
    });
    const [initialized, setInitialized] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const responseData = await ApiService.genderDashboard.getGenderDistribution(filter);

            if (responseData.activeSchoolYear && !activeSchoolYear) {
                setActiveSchoolYear(responseData.activeSchoolYear);

                if (!initialized && filter.schoolYears.length === 0) {
                    setFilter(prev => ({
                        ...prev,
                        schoolYears: [responseData.activeSchoolYear]
                    }));
                    setInitialized(true);
                    return;
                }
            }

            setData(responseData.data || []);
            setTotalsByYear(responseData.totalsByYear || []);
            setGrandTotal(responseData.grandTotal || null);
        } catch (err) {
            setError('Failed to load gender data');
            console.error('Error fetching gender data:', err);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (initialized) {
            fetchData();
        }
    }, [filter]);

    const updateFilter = (updates: Partial<GenderDashboardFilter>) => {
        setFilter(prev => ({ ...prev, ...updates }));
    };

    const refetch = () => {
        fetchData();
    };

    return (
        <GenderDataContext.Provider
            value={{
                data,
                loading,
                error,
                filter,
                activeSchoolYear,
                totalsByYear,
                grandTotal,
                updateFilter,
                refetch,
            }}
        >
            {children}
        </GenderDataContext.Provider>
    );
};

export const useGenderData = () => {
    const context = useContext(GenderDataContext);
    if (!context) {
        throw new Error('useGenderData must be used within GenderDataProvider');
    }
    return context;
};